import { useEffect } from 'react';

import {
	extractInstruction,
	type Instruction,
} from '@atlaskit/navigation-system/side-nav-items/drag-and-drop/hitbox';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import type { DropTargetRecord } from '@atlaskit/pragmatic-drag-and-drop/types';

import type { GlobalShortcutsDragAndDropChangeEvent, GlobalShortcutsItem } from '../../types';

type ProcessInstructionType = {
	destination: DropTargetRecord;
	instruction: Instruction;
	shortcuts: GlobalShortcutsItem[];
	source: Record<string, unknown>;
};

const SUPPORTED_DRAG_AND_DROP_SOURCES = {
	SHORTCUT_LINK_ITEM: 'shortcut-link-item',
	SHORTCUT_EXPAND_ITEM: 'shortcut-expand-item', // Not yet supported
} as const;

/**
 * Process drag and drop instructions for global shortcuts.
 * @param param0 - The parameters for processing the instruction.
 * @returns The new index of shortcuts after processing the instruction.
 *  eg: [0,1,2] becomes [1,0,2]
 */
const processInstruction = ({
	instruction,
	source,
	destination,
	shortcuts,
}: ProcessInstructionType) => {
	// Get current index order as an array
	let indexes = new Array(shortcuts.length).fill(0).map((_, index) => index);

	// Find indexes of drop target
	const sourceIndex = shortcuts.findIndex((item) => item.label === source.label);
	const destinationIndex = shortcuts.findIndex((item) => item.label === destination.data.label);

	// Determine early exits
	if (sourceIndex === -1 || destinationIndex === -1) {
		return;
	}
	// Determine whether indexes is the same
	if (sourceIndex === destinationIndex) {
		return;
	}

	// Process instruction based on operation
	switch (instruction.operation) {
		case 'reorder-after': {
			return reorder({
				list: indexes,
				startIndex: sourceIndex,
				finishIndex: destinationIndex,
			});
		}
		case 'reorder-before': {
			return reorder({
				list: indexes,
				startIndex: sourceIndex,
				finishIndex: destinationIndex,
			});
		}
	}

	return;
};

export function useDragAndDropSave({
	enabled,
	shortcuts,
	handleDragAndDropChange,
}: {
	enabled: boolean;
	handleDragAndDropChange?: GlobalShortcutsDragAndDropChangeEvent;
	shortcuts: GlobalShortcutsItem[];
}) {
	useEffect(() => {
		if (!enabled) {
			return;
		}
		return monitorForElements({
			canMonitor: ({ source }) =>
				source.data.type === SUPPORTED_DRAG_AND_DROP_SOURCES.SHORTCUT_LINK_ITEM,
			onDrop({ source, location }) {
				if (!shortcuts) {
					return;
				}

				const dragging = source.data;
				const [innerMost] = location.current.dropTargets;

				if (!innerMost) {
					return;
				}
				const dropTargetData = innerMost.data;

				const instruction: Instruction | null = extractInstruction(dropTargetData);
				if (!instruction) {
					return;
				}

				// Process instruction based on operation
				const newOrder = processInstruction({
					instruction,
					source: dragging,
					destination: innerMost,
					shortcuts,
				});

				// Call the change handler with the new order if it exists
				if (handleDragAndDropChange && newOrder) {
					handleDragAndDropChange(newOrder);
				}
			},
		});
	}, [enabled, shortcuts, handleDragAndDropChange]);
}
